import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  alpha,
  Box,
  useTheme,
  FormHelperText
} from '@mui/material';
import { EmailNotification } from './index';
import { httpRequest } from 'src/services/httpRequest';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { BidFormErrors } from '../Create-Bid';

interface EmailNotificationFormProps {
  emailData: EmailNotification;
  setEmailData: React.Dispatch<React.SetStateAction<EmailNotification>>;
  goLiveDate: Date | null;
  errors: BidFormErrors & { content?: string }; // ensure content is optional on type
  setErrors: React.Dispatch<
    React.SetStateAction<BidFormErrors & { content?: string }>
  >;
}

const EmailNotificationForm: React.FC<EmailNotificationFormProps> = ({
  emailData,
  setEmailData,
  goLiveDate,
  errors,
  setErrors
}) => {
  const isGoLiveDateFilled = goLiveDate !== null && goLiveDate !== undefined;
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const hasContentError = !!errors?.content;

  // Fetch email template data when component mounts
  useEffect(() => {
    const fetchEmailTemplate = async () => {
      setLoading(true);

      try {
        const response = await httpRequest(
          'GET',
          '/bid_portal/template/email?Form=UpdateBid&Event=Update',
          {},
          {}
        );

        if (response.status === 'success') {
          const templates = response.data?.data?.data || response.data?.data;

          if (templates && Array.isArray(templates) && templates.length > 0) {
            const templateData = templates[0];

            setEmailData({
              subject: templateData.EmailSubject || '',
              content: templateData.EmailBody || '',
              saveType: emailData.saveType || ''
            });

            const contentBlock = htmlToDraft(templateData.EmailBody || '');
            const contentState = ContentState.createFromBlockArray(
              contentBlock.contentBlocks
            );
            setEditorState(EditorState.createWithContent(contentState));

            // Clear content error if template provided content
            if (templateData.EmailBody && errors.content) {
              setErrors((prev) => ({ ...prev, content: '' }));
            }
          } else {
            console.warn('No template data found in response');
          }
        } else {
          console.error('Failed to fetch email template:', response);
        }
      } catch (fetchError: any) {
        console.error('Error fetching email template:', fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchEmailTemplate();
  }, []); // run once

  // Convert editor state to HTML and update emailData
  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const contentState = newEditorState.getCurrentContent();
    const hasText = contentState.hasText();

    const htmlContent = draftToHtml(convertToRaw(contentState));
    setEmailData((prev) => ({
      ...prev,
      content: htmlContent
    }));

    // If user starts typing, clear content error
    if (errors.content && hasText) {
      setErrors((prev) => ({ ...prev, content: '' }));
    }
  };

  // Loading state while data is being fetched
  if (loading) {
    return (
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}
        >
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email Subject"
          error={!!errors.subject}
          helperText={errors.subject}
          value={emailData.subject}
          onChange={(e) => {
            setEmailData({ ...emailData, subject: e.target.value });
            if (errors.subject && e.target.value.trim() !== '') {
              setErrors((prev) => ({ ...prev, subject: '' }));
            }
          }}
        />
      </Grid>

      {/* Editor with error handling */}
      <Grid item xs={12}>
        <FormControl
          fullWidth
          error={hasContentError}
          component="fieldset"
          variant="standard"
        >
          <Box
            sx={{
              border: `1px solid ${
                hasContentError
                  ? theme.palette.error.main
                  : alpha(theme.palette.primary.main, 0.23)
              }`,
              borderRadius: 1,
              '& .rdw-editor-wrapper': {
                backgroundColor: 'transparent'
              },
              '& .rdw-editor-toolbar': {
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.23
                )}`,
                borderRadius: '4px 4px 0 0',
                padding: '6px',
                backgroundColor: alpha(theme.palette.background.default, 0.5)
              },
              '& .rdw-editor-main': {
                padding: '12px',
                minHeight: '250px',
                fontSize: '14px',
                lineHeight: '1.5'
              },
              '& .rdw-option-wrapper': {
                border: 'none',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }
              },
              '& .rdw-option-active': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorChange}
              placeholder="Enter email content..."
              toolbar={{
                options: ['inline', 'list', 'textAlign', 'history'],
                inline: { options: ['bold', 'italic', 'underline'] },
                list: { options: ['unordered', 'ordered'] },
                textAlign: { options: ['left', 'center', 'right'] }
              }}
            />
          </Box>
          {errors?.content && <FormHelperText>{errors.content}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset" fullWidth error={!!errors.saveType}>
          <InputLabel>Notification Options</InputLabel>
          <Select
            value={emailData.saveType}
            onChange={(e) => {
              setEmailData({
                ...emailData,
                saveType: e.target.value as EmailNotification['saveType']
              });
              if (errors.saveType && (e.target.value as string).trim() !== '') {
                setErrors((prev) => ({ ...prev, saveType: '' }));
              }
            }}
            label="Notification Options"
          >
            <MenuItem value="Update and Notify" disabled={isGoLiveDateFilled}>
              Update and Notify
            </MenuItem>
            <MenuItem
              value="Update and Notify Later"
              disabled={!isGoLiveDateFilled}
            >
              Update and Notify Later
            </MenuItem>
            <MenuItem value="Update">Update</MenuItem>
          </Select>
          {errors.saveType && (
            <FormHelperText>{errors.saveType}</FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default EmailNotificationForm;
